import { Box, Typography, useTheme } from '@mui/material';
import { TournamentStatus } from '@okletsplay/core';
import { KnockoutTournamentEntity, TournamentEntity } from 'app/interfaces/entity';
import { TournamentCheckinTimer } from 'modules/tournaments/components/TournamentCheckinTimer';
import moment from 'moment/moment';
import React, { FC, useMemo } from 'react';
import { getAssetTransformerUrl } from 'shared/utils';
import { useTournamentPlatformType } from '../../hooks';
import { useStyles } from './TournamentBanner.styles';
import { Picture, ResponsiveImageSrc } from 'shared/components/Picture';

interface TournamentBannerProps {
  tournament: TournamentEntity;
}

export const TournamentBanner: FC<TournamentBannerProps> = ({ tournament }) => {
  const theme = useTheme();
  const height = parseFloat(theme.spacing(23.75));
  const { checkIsKnockoutEnabled } = useTournamentPlatformType();

  const [defaultSrc, ...sources] = useMemo((): ResponsiveImageSrc[] => {
    if (!tournament || !tournament.game?.bannerImage) return [];

    const gameBannerKey = tournament.game.bannerImage.split('.com/')[1];
    const key = tournament.bannerImageKey || gameBannerKey;

    const getSrc = (density = 1): string => {
      if (density === 2) {
        return getAssetTransformerUrl(key);
      }
      return getAssetTransformerUrl(key, {
        resize: { height: height * density },
      });
    };

    return [
      {
        getSrc,
      },
      {
        densities: [1, 1.5, 2],
        getSrc,
      },
    ];
  }, [tournament, height]);

  const { classes } = useStyles();
  const gameTitle = useMemo(() => tournament?.game?.title, [tournament?.game?.title]);

  const checkinTimerDate = useMemo(() => {
    const isKnockoutTournament = checkIsKnockoutEnabled(tournament?.game?.platformType);
    if (!isKnockoutTournament) {
      return '';
    }

    if (tournament.status === TournamentStatus.Canceled) {
      return '';
    }

    const currentDate = moment();
    const subMatchesSettings = (tournament as KnockoutTournamentEntity).subMatchesSettings;
    const subMatchSettings = subMatchesSettings?.find((subMatch) =>
      currentDate.isBefore(moment(subMatch.startedAt))
    );

    return subMatchSettings ? subMatchSettings.startedAt : tournament?.startedAt;
  }, [checkIsKnockoutEnabled, tournament]);

  const bannerTitle = useMemo(() => {
    if (tournament.status === TournamentStatus.Canceled) {
      return `${tournament?.name} (${tournament.status})`;
    }

    return tournament?.name;
  }, [tournament?.name, tournament.status]);

  return (
    <Box className={classes.root}>
      <Picture defaultSrc={defaultSrc} sources={sources} />
      <Box className={classes.textContainer}>
        {gameTitle && <Typography className={classes.gameTitle}>{gameTitle}</Typography>}
        {bannerTitle && <Typography className={classes.title}>{bannerTitle}</Typography>}
        {checkinTimerDate && <TournamentCheckinTimer date={checkinTimerDate} />}
      </Box>
    </Box>
  );
};
